import { clubPath, clubUrl } from '../clubhouse';
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { orgs, auth } from '../services/api';
import { 
  LayoutDashboard, MapPin, Package, Droplet, Users, ListTodo, 
  Calendar, Droplets, Wrench, Leaf, DollarSign, Euro,
  Menu, X, LogOut, User, ClipboardList, Bot, Sparkles, Building2, ChevronDown, Check, Globe, Settings, ExternalLink, Flag
} from 'lucide-react';
import AlexWidget from './AlexWidget';
import WeatherAlertBell from './WeatherAlertBell';
import { toast } from 'sonner';

const Layout = ({ children }) => {
  const { user, logout, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  // State pour gérer l'ouverture de la sidebar mobile
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // State pour gérer la sidebar réduite sur desktop
  const [desktopCollapsed, setDesktopCollapsed] = useState(false);
  // State pour le sélecteur de golf
  const [organizations, setOrganizations] = useState([]);
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const [switchingOrg, setSwitchingOrg] = useState(false);

  // Charger les organisations pour le super admin
  useEffect(() => {
    if (user?.is_super_admin) {
      orgs.list().then(res => {
        setOrganizations(res.data || []);
      }).catch(err => console.error('Erreur chargement orgs:', err));
    }
  }, [user?.is_super_admin]);

  // Fermer le menu mobile quand on change de page
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Changer d'organisation
  const handleSwitchOrg = async (orgId) => {
    if (switchingOrg) return;
    setSwitchingOrg(true);
    try {
      await auth.switchOrganization(orgId);
      const org = organizations.find(o => o.id === orgId);
      toast.success(`Basculé vers ${org?.name || 'le golf'}`);
      setShowOrgDropdown(false);
      // Recharger les données utilisateur et la page
      if (refreshUser) await refreshUser();
      window.location.reload();
    } catch (error) {
      console.error('Erreur switch org:', error);
      toast.error('Erreur lors du changement de golf');
    } finally {
      setSwitchingOrg(false);
    }
  };

  // Map des path entretien → id page_permissions
  const entretienPathToPageId = {
    '/entretien': 'dashboard',
    '/alex': 'dashboard', // toujours visible
    '/planning': 'planning',
    '/agronomic': 'agronomic',
    '/water': 'water',
    '/applications': 'applications',
    '/expenses-budget': 'expenses-budget',
    '/machines': 'machines',
    '/calendar': 'calendar',
    '/tasks-surfaces': 'tasks-surfaces',
    '/maintenance-settings': 'maintenance-settings',
  };
  
  const entretienPagePerms = user?.page_permissions?.entretien || [];
  const hasEntretienRestrictions = entretienPagePerms.length > 0;
  const isAdmin = user?.user_role === 'admin';
  
  const filterItems = (items) => items.filter(item => {
    if (isAdmin) return true;
    if (!hasEntretienRestrictions) return true;
    const pageId = entretienPathToPageId[item.path];
    if (!pageId) return true;
    return entretienPagePerms.includes(pageId);
  });

  // Rediriger si la page courante n'est pas autorisée
  useEffect(() => {
    if (isAdmin || !hasEntretienRestrictions) return;
    const pageId = entretienPathToPageId[location.pathname];
    if (!pageId) return;
    if (!entretienPagePerms.includes(pageId)) {
      // Trouver la première page autorisée
      const firstAllowed = Object.entries(entretienPathToPageId).find(
        ([, id]) => entretienPagePerms.includes(id)
      );
      if (firstAllowed) {
        navigate(firstAllowed[0], { replace: true });
      }
    }
  }, [location.pathname, isAdmin, hasEntretienRestrictions, entretienPagePerms, navigate]);

  // Menu organisé par sections
  const menuSections = [
    {
      title: null,
      items: filterItems([
        { path: '/entretien', icon: LayoutDashboard, label: 'Dashboard' },
      ])
    },
    {
      title: null,
      items: [
        { path: '/alex', icon: Leaf, label: 'BASS IA', highlight: true },
      ]
    },
    {
      title: 'Opérationnel',
      items: filterItems([
        { path: '/planning', icon: ClipboardList, label: 'Planning RH' },
        { path: '/agronomic', icon: Leaf, label: 'Mesures' },
        { path: '/water', icon: Droplets, label: 'Eau' },
        { path: '/applications', icon: Droplet, label: 'Traitement' },
        { path: '/expenses-budget', icon: Euro, label: 'Dépenses et Budget' },
        { path: '/machines', icon: Wrench, label: 'Machines' },
        { path: '/bunker-maintenance', icon: Flag, label: 'Bunkers' },
      ])
    },
    {
      title: 'Organisation',
      items: filterItems([
        { path: '/calendar', icon: Calendar, label: 'Calendrier' },
        { path: '/tasks-surfaces', icon: ListTodo, label: 'Tâches et Surfaces' },
        { path: '/maintenance-settings', icon: Settings, label: 'Paramètres' },
      ])
    },
  ].filter(s => s.items.length > 0);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Navigation et profil utilisateur - réutilisables
  const NavItems = ({ showLabels = true }) => (
    <nav className="flex-1 overflow-y-auto p-2">
      {menuSections.map((section, sectionIndex) => (
        <div key={sectionIndex} className={section.title ? 'mt-3 first:mt-0' : ''}>
          {section.title && showLabels && (
            <p className="px-3 py-1.5 text-xs font-semibold text-neutral-400 uppercase tracking-wider">
              {section.title}
            </p>
          )}
          {section.title && !showLabels && (
            <div className="border-t border-neutral-200 my-2 mx-2"></div>
          )}
          
          {section.items.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg mb-0.5 transition-colors text-sm ${
                  isActive 
                    ? 'bg-primary/10 text-primary border-l-4 border-primary' 
                    : 'text-neutral-600 hover:bg-neutral-100'
                }`}
                title={!showLabels ? item.label : ''}
              >
                <Icon size={18} />
                {showLabels && <span className="font-medium">{item.label}</span>}
              </Link>
            );
          })}
        </div>
      ))}
      {/* Lien Portail Client */}
      <div className="mt-4 pt-3 border-t border-neutral-200 mx-1">
        <a
          href={clubPath('/')}
          className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition-colors border border-emerald-200"
          data-testid="admin-portal-link"
        >
          <ExternalLink size={18} />
          {showLabels && <span className="font-medium">Portail Client</span>}
        </a>
      </div>
    </nav>
  );

  const UserFooter = ({ showLabels = true }) => (
    <div className="p-3 border-t border-neutral-200">
      <div className={`flex items-center gap-3 px-3 py-2 ${showLabels ? '' : 'justify-center'}`}>
        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
          <User size={16} className="text-primary" />
        </div>
        {showLabels && (
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.first_name} {user?.last_name}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
        )}
      </div>
      <div className="flex gap-1 mt-2">
        <a
          href={clubPath('/')}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-xs text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors border border-emerald-200"
          data-testid="admin-portal-link"
        >
          <ExternalLink size={14} />
          {showLabels && <span>Portail</span>}
        </a>
        <button
          onClick={handleLogout}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-xs text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-red-100"
        >
          <LogOut size={14} />
          {showLabels && <span>Quitter</span>}
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen md:h-screen bg-neutral-50">
      {/* Mobile: Overlay quand menu ouvert */}
      {mobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile: Sidebar en drawer */}
      <aside className={`
        md:hidden fixed inset-y-0 left-0 z-50 w-72 bg-white border-r border-neutral-200 
        transform transition-transform duration-300 ease-in-out flex flex-col pt-[env(safe-area-inset-top)]
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Mobile Sidebar Header */}
        <div className="flex items-center justify-between p-3 border-b border-neutral-200">
          <Link to="/modules" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <img src="/bass-logo.png" alt="BASS GOLF" className="w-8 h-8 rounded-lg object-cover" />
            <div>
              <h1 className="text-base font-semibold text-primary">BASS GOLF</h1>
              <p className="text-[9px] text-neutral-400 uppercase tracking-wider -mt-0.5">management</p>
            </div>
          </Link>
          <div className="flex items-center gap-1">
            <a href={clubPath('/')} className="p-2 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700" data-testid="admin-portal-link-mobile">
              <ExternalLink size={18} />
            </a>
            <button onClick={() => setMobileMenuOpen(false)} className="p-1.5 rounded-lg hover:bg-neutral-100" aria-label="Fermer le menu">
              <X size={20} />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          <NavItems showLabels={true} />
        </div>
        <UserFooter showLabels={true} />
      </aside>

      {/* Desktop: Sidebar fixe */}
      <aside className={`
        hidden md:flex flex-col relative bg-white border-r border-neutral-200 
        transition-all duration-300 h-full max-h-screen
        ${desktopCollapsed ? 'w-16' : 'w-64'}
      `}>
        {/* Desktop Sidebar Header */}
        <div className="p-3 border-b border-neutral-200 flex items-center justify-between">
          {!desktopCollapsed ? (
            <div className="flex items-center gap-2">
              <Link to="/modules" className="hover:opacity-80 transition-opacity">
                <img src="/bass-logo.png" alt="BASS GOLF" className="w-8 h-8 rounded-lg object-cover" />
              </Link>
              <div>
                <Link to="/modules" className="hover:opacity-80 transition-opacity">
                  <h1 className="text-base font-semibold text-primary">BASS GOLF</h1>
                </Link>
                <p className="text-[9px] text-neutral-400 uppercase tracking-wider -mt-0.5">management</p>
                {/* Sélecteur de golf pour super admin */}
                {user?.is_super_admin ? (
                  <div className="relative">
                    <button
                      onClick={() => setShowOrgDropdown(!showOrgDropdown)}
                      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
                    >
                      <Building2 size={10} />
                      <span className="truncate max-w-[100px]">{user?.organization_name || 'Sélectionner'}</span>
                      <ChevronDown size={10} />
                    </button>
                    
                    {showOrgDropdown && (
                      <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-neutral-200 rounded-lg shadow-lg z-50 py-1">
                        <div className="px-3 py-2 border-b border-neutral-100">
                          <p className="text-xs font-semibold text-neutral-500">CHANGER DE GOLF</p>
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {organizations.map(org => (
                            <button
                              key={org.id}
                              onClick={() => handleSwitchOrg(org.id)}
                              disabled={switchingOrg}
                              className={`w-full px-3 py-2 text-left text-sm hover:bg-neutral-50 flex items-center justify-between ${
                                org.id === user?.organization_id ? 'bg-primary/5 text-primary' : 'text-neutral-700'
                              }`}
                            >
                              <span className="truncate">{org.name}</span>
                              {org.id === user?.organization_id && <Check size={14} className="text-primary" />}
                            </button>
                          ))}
                        </div>
                        <div className="border-t border-neutral-100 mt-1 pt-1">
                          <Link
                            to="/all-golfs"
                            onClick={() => setShowOrgDropdown(false)}
                            className="w-full px-3 py-2 text-left text-sm text-primary hover:bg-primary/5 flex items-center gap-2"
                          >
                            <Globe size={14} />
                            Voir tous les golfs
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">{user?.organization_name || 'Golf'}</p>
                )}
              </div>
            </div>
          ) : (
            <Link to="/modules" className="hover:opacity-80 transition-opacity mx-auto">
              <img src="/bass-logo.png" alt="BASS" className="w-8 h-8 rounded-lg object-cover" />
            </Link>
          )}
          <button
            onClick={() => setDesktopCollapsed(!desktopCollapsed)}
            className={`p-1.5 rounded-lg hover:bg-neutral-100 ${desktopCollapsed ? 'mx-auto' : ''}`}
            aria-label={desktopCollapsed ? "Agrandir le menu" : "Réduire le menu"}
          >
            {desktopCollapsed ? <Menu size={18} /> : <X size={18} />}
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <NavItems showLabels={!desktopCollapsed} />
        </div>
        <UserFooter showLabels={!desktopCollapsed} />
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col md:overflow-y-auto overflow-x-hidden">
        {/* Mobile: Header fixe simplifié avec hamburger */}
        <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-neutral-200 px-3 py-2 flex items-center justify-between" style={{ paddingTop: 'max(0.5rem, env(safe-area-inset-top))' }}>
          {/* Bouton hamburger */}
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="p-2 rounded-lg hover:bg-neutral-100"
            aria-label="Ouvrir le menu"
          >
            <Menu size={22} />
          </button>
          
          <Link to="/modules" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <img src="/bass-logo.png" alt="BASS GOLF" className="w-7 h-7 rounded-lg object-cover" />
            <div>
              <span className="font-semibold text-primary block text-sm">BASS GOLF</span>
              <span className="text-[8px] text-neutral-400 uppercase tracking-wider -mt-1 block">management</span>
            </div>
          </Link>
          
          {/* Sélecteur de golf pour super admin sur mobile */}
          <div className="flex items-center gap-1">
            <WeatherAlertBell userId={user?.id} />
            {user?.is_super_admin ? (
            <div className="relative">
              <button
                onClick={() => setShowOrgDropdown(!showOrgDropdown)}
                className="flex items-center gap-1 px-2 py-1.5 bg-neutral-100 hover:bg-neutral-200 rounded-lg transition-colors text-xs"
              >
                <Building2 size={14} className="text-neutral-500" />
                <ChevronDown size={12} className="text-neutral-400" />
              </button>

              {showOrgDropdown && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowOrgDropdown(false)} />
                  <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-neutral-200 rounded-lg shadow-xl z-50 py-1">
                    <div className="px-3 py-2 border-b border-neutral-100">
                      <p className="text-xs font-semibold text-neutral-500 uppercase">Changer de golf</p>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {organizations.map(org => (
                        <button
                          key={org.id}
                          onClick={() => handleSwitchOrg(org.id)}
                          disabled={switchingOrg}
                          className={`w-full px-3 py-2 text-left text-sm hover:bg-neutral-50 flex items-center justify-between ${
                            org.id === user?.organization_id ? 'bg-primary/5 text-primary' : 'text-neutral-700'
                          }`}
                        >
                          <span className="truncate">{org.name}</span>
                          {org.id === user?.organization_id && <Check size={14} className="text-primary" />}
                        </button>
                      ))}
                    </div>
                    <div className="border-t border-neutral-100 mt-1 pt-1">
                      <Link
                        to="/all-golfs"
                        onClick={() => setShowOrgDropdown(false)}
                        className="w-full px-3 py-2 text-left text-sm text-primary hover:bg-primary/5 flex items-center gap-2"
                      >
                        <Globe size={14} />
                        Voir tous les golfs
                      </Link>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="w-10"></div> 
          )}
          </div>
        </div>

        {/* Desktop: Header fixe simple */}
        <div className="hidden md:flex fixed top-0 left-64 right-0 z-30 bg-white border-b border-neutral-200 h-12 items-center px-6 justify-between"
          style={{ left: desktopCollapsed ? '4rem' : '16rem' }}
        >
          <div className="flex items-center gap-2">
            <Leaf size={18} className="text-green-600" />
            <span className="font-medium text-neutral-700">Module Entretien</span>
          </div>
          <div className="flex items-center gap-2">
            <WeatherAlertBell userId={user?.id} />
          </div>
        </div>

        {/* Spacer for fixed header on mobile - dynamique selon safe-area */}
        <div className="md:hidden flex-shrink-0" style={{ height: 'calc(3.5rem + env(safe-area-inset-top, 0px))' }}></div>
        
        {/* Spacer for fixed header on desktop */}
        <div className="hidden md:block h-12 flex-shrink-0"></div>

        {/* Page content */}
        <div className="flex-1 w-full max-w-[1400px] mx-auto px-3 py-4 md:p-6 pb-24 md:pb-6 overflow-x-hidden">
          {children}
        </div>
      </main>

      {/* Widget Alex flottant */}
      <AlexWidget />
    </div>
  );
};

export default Layout;
